// @flow
import * as React from 'react';
import Button, {ButtonLink} from 'sail/Button';
import type {IconType} from 'sail/Icon';

import {defineMessages, FormattedMessage} from 'react-intl';

import {useAnalytics, usePrefs} from 'docs/data';
import {useFile, useFileDownload} from 'docs/data/integration-builder';

import './DownloadButton.scss';
import {getFileText} from 'docs/util';

const messages = defineMessages({
  couldNotDownload: {
    id: 'docsApp.integrationBuilder.code.couldNotDownload',
    description: 'Could not download label',
    defaultMessage: 'Could not download',
  },
  downloaded: {
    id: 'docsApp.integrationBuilder.code.downloaded',
    description: 'Downloaded label',
    defaultMessage: 'Downloaded',
  },
  downloading: {
    id: 'docsApp.integrationBuilder.code.downloading',
    description: 'Downloading label',
    defaultMessage: 'Downloading',
  },
  download: {
    id: 'docsApp.integrationBuilder.code.download',
    description: 'Download label',
    defaultMessage: 'Download',
  },
  downloadProject: {
    id: 'docsApp.integrationBuilder.code.downloadProject',
    description: 'Download project label',
    defaultMessage: 'Download project',
  },
  downloadFullApp: {
    id: 'docsApp.integrationBuilder.code.downloadFullApp',
    description: 'Download full app',
    defaultMessage: 'Download full app',
  },
});

type UseDownloadResult = {|
  label: ?React.Element<FormattedMessage>,
  icon: IconType,
  isDownloading: boolean,
  onClick: () => void,
|};

const useDownload = (): UseDownloadResult => {
  const {isDownloading, downloadFiles, success, error} = useFileDownload();
  const {components, contentRefs} = useFile();
  const {trackAction} = useAnalytics();
  const {getPref} = usePrefs();

  let label;
  let icon = 'download';
  if (isDownloading) {
    label = <FormattedMessage {...messages.downloading} />;
  } else if (!success && error) {
    icon = 'cross';
    label = <FormattedMessage {...messages.couldNotDownload} />;
  } else if (success) {
    icon = 'check';
    label = <FormattedMessage {...messages.downloaded} />;
  }

  const onClick = () => {
    const fileContent = {};

    Object.keys(components).forEach((filename) => {
      fileContent[filename] = getFileText(contentRefs[filename]);
    });

    downloadFiles(fileContent);
    trackAction(`integration_builder_download_files`, {
      platform_lang: getPref('platform') || 'none',
      client_lang: getPref('client') || 'none',
      server_lang: getPref('lang') || 'none',
    });
  };

  return {
    label,
    icon,
    onClick,
    isDownloading,
  };
};

export const DownloadButton = () => {
  const defaultLabel = <FormattedMessage {...messages.downloadFullApp} />;
  const {label, icon, onClick, isDownloading} = useDownload();

  return (
    <Button
      icon={icon}
      label={label || defaultLabel}
      color={isDownloading ? 'red' : 'blue'}
      disabled={isDownloading}
      onClick={onClick}
    />
  );
};
export const DownloadButtonLink = () => {
  const defaultLabel = <FormattedMessage {...messages.download} />;
  const {label, icon, onClick} = useDownload();

  return (
    <ButtonLink
      className="DownloadButton--button-link"
      icon={icon}
      iconPosition="right"
      label={label || defaultLabel}
      size="small"
      onClick={onClick}
    />
  );
};
